// src/utils/ecuadorTime.js
// Hora de Ecuador confiable basada en /api/users/time/now (servidor) + reloj monotónico.
// No depende del reloj del PC una vez sincronizado.

export const ECUADOR_TZ = "America/Guayaquil";
const LS_KEY = "ec_time_offset_v1"; // { offsetMs, savedAt }

let _baseServerMs = null; // epoch del servidor al sincronizar
let _basePerf = null;     // performance.now() al sincronizar

const hasPerf = () => typeof performance !== "undefined" && typeof performance.now === "function";

/** Fija el epoch confiable (ms) y guarda offset como respaldo para recargas. */
export function setTrustedEpoch(serverEpochMs) {
  if (!Number.isFinite(serverEpochMs)) return;
  _baseServerMs = serverEpochMs;
  _basePerf = hasPerf() ? performance.now() : 0;
  try {
    if (typeof localStorage !== "undefined") {
      const offsetMs = serverEpochMs - Date.now();
      localStorage.setItem(LS_KEY, JSON.stringify({ offsetMs, savedAt: Date.now() }));
    }
  } catch {}
}

/** Epoch ms confiable (no afectado por cambios manuales del reloj del cliente). */
export function nowTrustedMs() {
  if (_baseServerMs != null && hasPerf()) {
    return _baseServerMs + (performance.now() - _basePerf);
  }
  // Fallback: offset persistido entre recargas
  try {
    if (typeof localStorage !== "undefined") {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) {
        const { offsetMs } = JSON.parse(raw);
        if (Number.isFinite(offsetMs)) return Date.now() + offsetMs;
      }
    }
  } catch {}
  // Último recurso si aún no hay sync:
  return Date.now();
}

export function nowTrustedDate() {
  return new Date(nowTrustedMs());
}

/** YYYY-MM-DD según zona horaria de Ecuador usando tiempo confiable. */
export function getEcuadorYMD(date = nowTrustedDate()) {
  const dtf = new Intl.DateTimeFormat("es-EC", {
    timeZone: ECUADOR_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const parts = Object.fromEntries(dtf.formatToParts(date).map(p => [p.type, p.value]));
  return `${parts.year}-${parts.month}-${parts.day}`;
}

/** HH:mm:ss Ecuador (útil si lo necesitas) */
export function getEcuadorHMS(date = nowTrustedDate()) {
  const dtf = new Intl.DateTimeFormat("es-EC", {
    timeZone: ECUADOR_TZ,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  return dtf.format(date);
}

/** Hoy (YYYY-MM-DD) según Ecuador con tiempo confiable. */
export const todayEcuadorYMD = () => getEcuadorYMD();

/** ¿Mismo día en Ecuador? */
export function isSameEcuadorDay(a, b) {
  return getEcuadorYMD(a) === getEcuadorYMD(b);
}

/**
 * Sincroniza usando la función del contexto/servicio:
 * getTimeNow: () => Promise<{ nowUtcMs?: number, iso?: string }>
 */
export async function syncEcuadorTimeWith(getTimeNow) {
  try {
    const data = await getTimeNow();
    const ms = Number.isFinite(data?.nowUtcMs)
      ? data.nowUtcMs
      : (typeof data?.iso === "string" ? Date.parse(data.iso) : NaN);
    if (!Number.isFinite(ms)) return false;
    setTrustedEpoch(ms);
    return true;
  } catch {
    return false;
  }
}
